import { Card, CardBody, Table } from "reactstrap";
import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import PageHeader from "../common/PageHeader";
import withRedirect from "../../helpers/hoc/withRedirect";
import { fetchChartOfAccountsSummary } from "../../helpers/api/finance";
import { useSelector } from "react-redux";
import SupportLink from "../root/support/components/SupportLink";
import useSWRFetch from "../../helpers/hooks/useSWRFetch";
import { findOneConfig } from "../../helpers/api/condominium";
import ItemImageTitleSubProperty from "../common/ItemImageTitleSubProperty";

const ChartOfAccounts = () => {
  const reportRef = useRef();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { data: dataConfig, isLoading: loadingConfig } = useSWRFetch(
    selectedCondominium,
    findOneConfig
  );

  const { condominium, address, phone } = useSelector(
    (state) => state.condominium
  );

  const [chartOfAccountsSummary, setChartOfAccountsSummary] = useState([]);

  const fetchApiChartOfAccountsSummary = useCallback(async () => {
    try {
      const response = await fetchChartOfAccountsSummary(selectedCondominium);
      setChartOfAccountsSummary(response || []);
    } catch (error) {
      setChartOfAccountsSummary([]);
    }
  }, []);
  useEffect(() => {
    fetchApiChartOfAccountsSummary();
  }, [fetchApiChartOfAccountsSummary]);

  let bankCounter = 1;

  const bankRows =
    chartOfAccountsSummary.banks &&
    chartOfAccountsSummary.banks.map((bank, index) => {
      const bankCode = `${bankCounter}.${padZero(index + 1, 2)}.${padZero(
        bankCounter,
        2
      )}-${padZero(index + 1, 2)}`;
      bankCounter++;

      return (
        <tr key={index}>
          <td style={{ paddingLeft: "80px" }}>
            {bankCode} {bank.name}
          </td>
        </tr>
      );
    });

  let fundCounter = 1;

  const fundRows =
    chartOfAccountsSummary.reserveFunds &&
    chartOfAccountsSummary.reserveFunds.map((fund, index) => {
      const fundCode = `3.${padZero(fundCounter, 1)}.${padZero(
        fundCounter,
        1
      )}`;
      fundCounter++;

      return (
        <tr key={index}>
          <td style={{ paddingLeft: "40px" }}>
            {fundCode} {fund.name}
          </td>
        </tr>
      );
    });

  let ownerCounter = 1;

  const ownerRows =
    chartOfAccountsSummary.properties &&
    chartOfAccountsSummary.properties.map((owner, index) => {
      const ownerCode = `1.1.1.${padZero(ownerCounter, 2)}-${padZero(
        index + 1,
        2
      )}`;
      ownerCounter++;

      return (
        <tr key={index}>
          <td style={{ paddingLeft: "80px" }}>
            {ownerCode} {owner.user.data.firstname} {owner.user.data.lastname}
          </td>
        </tr>
      );
    });

  let incomeCounter = 1;

  const incomeRows =
    chartOfAccountsSummary.movementReserveFund &&
    chartOfAccountsSummary.movementReserveFund.map((income, index) => {
      const incomeCode = `4.1.02-${padZero(incomeCounter, 2)}`;
      incomeCounter++;

      return (
        <tr key={index}>
          <td style={{ paddingLeft: "60px" }}>
            {incomeCode} {income.name}
          </td>
        </tr>
      );
    });

  let accountCounter = 1;

  const accountRows =
    chartOfAccountsSummary.finances &&
    chartOfAccountsSummary.finances.map((account, index) => {
      const accountCode = `2.1.1.01-${padZero(accountCounter, 2)}`;
      accountCounter++;

      return (
        <tr key={index}>
          <td style={{ paddingLeft: "80px" }}>
            {accountCode} {account.name}
          </td>
        </tr>
      );
    });

  let financeCounter = 1;

  const financeRows =
    chartOfAccountsSummary.finances &&
    chartOfAccountsSummary.finances.map((finance, index) => {
      const financeCode = `6.1.01-${padZero(financeCounter, 2)}`;
      financeCounter++;

      return (
        <tr key={index}>
          <td style={{ paddingLeft: "60px" }}>
            {financeCode} {finance.name}
          </td>
        </tr>
      );
    });
  function padZero(number, length) {
    return String(number).padStart(length, "0");
  }

  return (
    <>
      <PageHeader
        title="Plan de cuentas"
        titleTag="h2"
        description="Gestiona y administra cada una de tus finanzas."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>

      <Card
        className="mb-3"
        innerRef={(e) => {
          reportRef.current = e;
        }}
      >
        <CardBody>
          <ItemImageTitleSubProperty
            image={dataConfig?.data?.logo}
            title1={"Plan de cuentas"}
            titleCondominium={`Condominio: ${condominium}`}
            condominium={[
              {
                label: "Documento de identificación",
                value: phone || "No disponible",
              },

              { label: "Dirección", value: address },
            ]}
            loading={loadingConfig}
          />
          <div className="table-responsive fs--1">
            <Table striped className="border-bottom">
              <thead>
                <tr>
                  <th>Cuenta</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1. ACTIVOS</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "20px" }}>
                    1.1 ACTIVOS CORRIENTES
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "40px" }}>
                    1.1.1 Activos Disponibles
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "60px" }}>1.1.1.01 Caja</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "80px" }}>
                    1.1.1.01-01 Caja Principal Condominio
                  </td>
                </tr>

                <tr>
                  <td style={{ paddingLeft: "40px" }}>
                    1.1.2 Activos Realizables:
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "60px" }}>
                    1.1.2.01 Efectos por cobrar:
                  </td>
                </tr>
                {ownerRows}

                <tr>
                  <td style={{ paddingLeft: "60px" }}>1.1.1.02 Bancos</td>
                </tr>
                {bankRows}

                <tr>
                  <td>2. PASIVOS</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "20px" }}>
                    2.1 PASIVOS CORRIENTES
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "40px" }}>
                    2.1.1 Cuentas por Pagar
                  </td>
                </tr>

                <tr>
                  <td style={{ paddingLeft: "60px" }}>
                    2.1.1.01 Cuentas por Pagar Servicios
                  </td>
                </tr>
                {accountRows}

                <tr>
                  <td>3. FONDO DE RESERVA</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "20px" }}>
                    3.1 Fondo de Reserva Aportado
                  </td>
                </tr>
                {fundRows}

                <tr>
                  <td>4. INGRESOS</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "20px" }}>
                    4.1 Ingresos por Condominio
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "40px" }}>
                    4.1.01 Ingresos Cuotas del Condominio
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "60px" }}>
                    4.1.01-01 Ingresos por Cuotas del Condominio Mensual
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "40px" }}>
                    4.1.02 Ingresos Fondos de Reserva
                  </td>
                </tr>
                {incomeRows}

                <tr>
                  <td>6. GASTOS</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "20px" }}>6.1 GASTOS OPERATIVOS</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: "40px" }}>
                    6.1.01 Gastos de Servicios
                  </td>
                </tr>
                {financeRows}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

ChartOfAccounts.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(ChartOfAccounts);
